/**
 * Minnesota State-Specific Diagnostic Questions
 * 
 * CRITICAL REGULATORY FRAMEWORK UPDATE:
 * Minnesota Rules Chapter 4668 was REPEALED in its entirety by:
 * - Laws 2013, Chapter 108, Article 11, Section 33
 * - Laws 2014, Chapter 275, Article 1, Section 134
 * 
 * Home care provider requirements are now found in Minnesota STATUTES,
 * specifically Minn. Stat. 144A.43 through 144A.484.
 * 
 * Current Applicable Statutes:
 * - Minn. Stat. 144A.43: Definitions
 * - Minn. Stat. 144A.44: Home Care Bill of Rights
 * - Minn. Stat. 144A.471: Home care provider license requirements
 * - Minn. Stat. 144A.472: Application and renewal
 * - Minn. Stat. 144A.474: Surveys and enforcement
 * - Minn. Stat. 144A.475: License actions
 * - Minn. Stat. 144A.476: Background studies
 * - Minn. Stat. 144A.479: Training requirements
 * - Minn. Stat. 144A.4791: Care services requirements
 * - Minn. Stat. 144A.482: Home management providers
 * - Minn. Stat. 144.057: Background study requirements
 * - Minn. Stat. 245C: Human services background studies (NETStudy 2.0)
 * - Minn. Stat. 626.557: Vulnerable adult maltreatment reporting
 * - Minn. Stat. 626.556: Child maltreatment reporting
 * 
 * Regulatory Body: Minnesota Department of Health (MDH)
 * Phone: (651) 201-4200
 * Website: https://www.health.state.mn.us/facilities/regulation/homecare
 * 
 * License Types:
 * - Basic Home Care License: Non-skilled assistive services
 * - Comprehensive Home Care License: Skilled nursing and therapy services
 * - Integrated License with HCBS Designation: Home care + 245D basic support services
 * 
 * NOTE: The previous version of this file incorrectly cited Minnesota Rules
 * Chapter 4668 (e.g., "Minn. R. 4668.0002", "Minn. R. 4668.0075", etc.).
 * These rules have been REPEALED and replaced by statutes.
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const MINNESOTA_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'MN-A1',
    itemId: 'A1',
    stateCode: 'MN',
    title: 'Home Care Provider License Status',
    description: 'Minnesota requires all home care providers to be licensed by MDH under Minn. Stat. 144A.43-144A.484',
    severity: 'high',
    regulationCode: '144A.471',
    regulationName: 'Licensure',
    agencyName: 'Minnesota Department of Health',
    agencyAbbr: 'MDH',
    agencyPhone: '(651) 201-4200',
    agencyUrl: 'https://www.health.state.mn.us/facilities/regulation/homecare',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Care Provider license from MDH?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Home care licenses must be renewed annually. Check your license status at www.health.state.mn.us',
      },
      {
        id: 'q2',
        question: 'What is your home care provider license expiration date?',
        inputType: 'date',
        helpText: 'Found on your MDH license certificate',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 45, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your MDH Home Care Provider license is current and active per Minn. Stat. 144A.471.',
      atRisk: 'Your home care license expires within 90 days. Submit your renewal application to MDH before expiration per Minn. Stat. 144A.472.',
      nonCompliant: 'Operating a home care agency in Minnesota without a valid MDH license violates Minn. Stat. 144A.471 and can result in civil penalties up to $10,000 per violation. Contact MDH immediately at (651) 201-4101.',
      unknown: 'Verify your home care license status at www.health.state.mn.us or call MDH at (651) 201-4101.',
    },
    stateNotes: [
      'Home care provider licenses are renewed annually per Minn. Stat. 144A.472',
      'Two license types: Basic (non-skilled) and Comprehensive (skilled nursing)',
      'Temporary licenses available for new providers',
      'Background studies required through NETStudy 2.0 for all staff per Minn. Stat. 144A.476',
      'License fees: Basic = $2,100, Comprehensive = $4,200',
    ],
  },

  {
    id: 'MN-A2',
    itemId: 'A2',
    stateCode: 'MN',
    title: 'License Classification (Minn. Stat. 144A.471)',
    description: 'Minnesota distinguishes between Basic and Comprehensive home care licenses',
    severity: 'high',
    regulationCode: '144A.471',
    regulationName: 'License Type',
    agencyName: 'Minnesota Department of Health',
    agencyAbbr: 'MDH',
    agencyPhone: '(651) 201-4200',
    agencyUrl: 'https://www.health.state.mn.us/facilities/regulation/homecare',
    questions: [
      {
        id: 'q1',
        question: 'What type of home care license do you hold?',
        inputType: 'select',
        options: [
          { value: 'comprehensive', label: 'Comprehensive Home Care License' },
          { value: 'basic', label: 'Basic Home Care License' },
          { value: 'integrated', label: 'Integrated License (with HCBS designation)' },
          { value: 'unsure', label: 'Not sure' },
        ],
        required: true,
        helpText: 'Comprehensive licenses allow skilled nursing; Basic allows only non-skilled assistive services',
      },
      {
        id: 'q2',
        question: 'Do you provide any skilled nursing or therapy services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - assistive/personal care only' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['comprehensive', 'integrated'] },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'basic' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'basic' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'unsure', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Your license classification matches the services you provide per Minn. Stat. 144A.471.',
      atRisk: 'Verify your license classification matches your services. Basic licenses cannot provide skilled nursing per Minn. Stat. 144A.471, subd. 6.',
      nonCompliant: 'You are providing skilled nursing services with a Basic license. This violates Minn. Stat. 144A.471, subd. 6-7. You must upgrade to a Comprehensive license immediately. Contact MDH at (651) 201-4101.',
      unknown: 'Verify your license classification at MDH.',
    },
    stateNotes: [
      'Basic License (Minn. Stat. 144A.471, subd. 6): Assistive tasks only - dressing, feeding, grooming, toileting, bathing, medication reminders, homemaking',
      'Comprehensive License (Minn. Stat. 144A.471, subd. 7): All basic services PLUS skilled nursing, therapy, delegated nursing tasks',
      'Comprehensive license requires an RN or licensed health professional on staff',
      'Integrated license allows provision of 245D basic support services under home care license',
    ],
  },

  {
    id: 'MN-A3',
    itemId: 'A3',
    stateCode: 'MN',
    title: 'Written Policies and Procedures',
    description: 'Minnesota requires specific written policies per Minn. Stat. 144A.479',
    severity: 'medium',
    regulationCode: '144A.479',
    regulationName: 'Policies',
    agencyName: 'Minnesota Department of Health',
    agencyAbbr: 'MDH',
    agencyPhone: '(651) 201-4200',
    agencyUrl: 'https://www.health.state.mn.us/facilities/regulation/homecare',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following Minnesota-required policies does your agency have documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'client_rights', label: 'Home Care Bill of Rights (Minn. Stat. 144A.44)' },
          { value: 'service_agreement', label: 'Written service agreement requirements' },
          { value: 'staff_duties', label: 'Staff duties and limitations' },
          { value: 'supervision', label: 'Supervision of unlicensed personnel' },
          { value: 'complaint', label: 'Complaint investigation procedures' },
          { value: 'background', label: 'Background study procedures (Minn. Stat. 144A.476)' },
          { value: 'training', label: 'Training requirements (Minn. Stat. 144A.479)' },
          { value: 'maltreatment', label: 'Maltreatment reporting procedures (Minn. Stat. 626.557)' },
          { value: 'records', label: 'Record retention policy' },
          { value: 'infection', label: 'Infection control policies' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets Minnesota requirements per Minn. Stat. 144A.479.',
      atRisk: 'Your policy manual is missing required sections. MDH surveyors will review your policies for completeness.',
      nonCompliant: 'Multiple required policies are missing. This is a common MDH survey deficiency. Develop written policies immediately.',
      unknown: 'Review your policies against Minn. Stat. 144A.479 requirements.',
    },
    stateNotes: [
      'Written policies required per Minn. Stat. 144A.479',
      'Home Care Bill of Rights must be posted and provided to all clients per Minn. Stat. 144A.44',
      'Maltreatment reporting procedures must reference Minn. Stat. 626.557',
      'Policies must be reviewed and updated as regulations change',
      'MDH surveys include review of policy documentation for completeness',
    ],
  },

  {
    id: 'MN-A4',
    itemId: 'A4',
    stateCode: 'MN',
    title: 'Client Service Agreements (Minn. Stat. 144A.4791)',
    description: 'Minnesota requires written service agreements with all clients',
    severity: 'high',
    regulationCode: '144A.4791',
    regulationName: 'Service Agreements',
    agencyName: 'Minnesota Department of Health',
    agencyAbbr: 'MDH',
    agencyPhone: '(651) 201-4200',
    agencyUrl: 'https://www.health.state.mn.us/facilities/regulation/homecare',
    questions: [
      {
        id: 'q1',
        question: 'Do you have current, signed service agreements with ALL clients?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all clients have signed current agreements' },
          { value: 'most', label: 'Most clients have signed agreements' },
          { value: 'some', label: 'Some agreements are missing or outdated' },
          { value: 'no', label: 'No formal service agreements in place' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your service agreements include all required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'services', label: 'Specific services to be provided' },
          { value: 'frequency', label: 'Frequency and duration of services' },
          { value: 'fees', label: 'Fees and payment terms' },
          { value: 'rights', label: 'Home Care Bill of Rights acknowledgment' },
          { value: 'complaint', label: 'Complaint procedures' },
          { value: 'termination', label: 'Termination procedures' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['some', 'no'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Client service agreements are current and properly executed per Minn. Stat. 144A.4791.',
      atRisk: 'Some client service agreements may be missing or incomplete. Review all client files.',
      nonCompliant: 'Client service agreements are missing. This violates Minn. Stat. 144A.4791.',
      unknown: 'Verify all client service agreements are current and complete.',
    },
    stateNotes: [
      'Written service agreements required per Minn. Stat. 144A.4791',
      'Must be signed before services begin',
      'Must include specific services, frequency, fees, and termination procedures',
      'Home Care Bill of Rights acknowledgment required',
      'Agreements must be updated when services change',
    ],
  },

  {
    id: 'MN-A5',
    itemId: 'A5',
    stateCode: 'MN',
    title: 'Incident Reporting Process',
    description: 'Minnesota requires documented incident reporting procedures',
    severity: 'high',
    regulationCode: '144A.474',
    regulationName: 'Incident Reporting',
    agencyName: 'Minnesota Department of Health',
    agencyAbbr: 'MDH',
    agencyPhone: '(651) 201-4200',
    agencyUrl: 'https://www.health.state.mn.us/facilities/regulation/homecare',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented incident reporting process?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, fully documented and followed consistently' },
          { value: 'informal', label: 'Yes, but informal / not fully documented' },
          { value: 'no', label: 'No incident reporting process in place' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you report serious incidents to MDH within required timeframes?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / not sure of requirements' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'notEquals',
          value: 'no',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'documented' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'informal', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Incident reporting process is documented and followed.',
      atRisk: 'Incident reporting process exists but may not be fully documented per Minn. Stat. 144A.474.',
      nonCompliant: 'No incident reporting process in place. This is required by Minn. Stat. 144A.474.',
      unknown: 'Verify your incident reporting procedures.',
    },
    stateNotes: [
      'Incident reporting required per Minn. Stat. 144A.474',
      'Serious incidents must be reported to MDH within required timeframes',
      'Maintain incident logs with date, description, and corrective action',
      'Maltreatment incidents must also be reported to MAARC at (844) 880-1574',
      'Internal investigation procedures must be documented',
    ],
  },

  {
    id: 'MN-A6',
    itemId: 'A6',
    stateCode: 'MN',
    title: 'Emergency Preparedness Plan',
    description: 'Minnesota requires documented emergency preparedness procedures',
    severity: 'medium',
    regulationCode: '144A.479',
    regulationName: 'Emergency Preparedness',
    agencyName: 'Minnesota Department of Health',
    agencyAbbr: 'MDH',
    agencyPhone: '(651) 201-4200',
    agencyUrl: 'https://www.health.state.mn.us/facilities/regulation/homecare',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented emergency preparedness plan?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, comprehensive written plan in place' },
          { value: 'partial', label: 'Partial plan, needs updating' },
          { value: 'no', label: 'No emergency preparedness plan' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'documented', result: 'COMPLIANT' },
      { condition: 'equals', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness plan is comprehensive and documented.',
      atRisk: 'Emergency preparedness plan is incomplete. Update to meet requirements.',
      nonCompliant: 'No emergency preparedness plan in place.',
      unknown: 'Review your emergency preparedness procedures.',
    },
    stateNotes: [
      'Emergency preparedness required per Minn. Stat. 144A.479',
      'Plan must address severe weather, power outages, and natural disasters',
      'Staff must know emergency contact procedures',
      'Client emergency information must be maintained and accessible',
      'Annual review and drill of emergency procedures recommended',
    ],
  },

  {
    id: 'MN-A7',
    itemId: 'A7',
    stateCode: 'MN',
    title: 'Complaint and Grievance Handling',
    description: 'Minnesota requires documented complaint handling procedures',
    severity: 'medium',
    regulationCode: '144A.474',
    regulationName: 'Complaints',
    agencyName: 'Minnesota Department of Health',
    agencyAbbr: 'MDH',
    agencyPhone: '(651) 201-4200',
    agencyUrl: 'https://www.health.state.mn.us/facilities/regulation/homecare',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented process for handling client complaints and grievances?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, documented process with tracking' },
          { value: 'informal', label: 'Informal process, not fully documented' },
          { value: 'no', label: 'No complaint handling process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you inform clients how to file complaints with MDH?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'documented' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'informal', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Complaint and grievance handling process is documented per Minn. Stat. 144A.474.',
      atRisk: 'Complaint handling process exists but may not be fully documented.',
      nonCompliant: 'No complaint handling process in place.',
      unknown: 'Verify your complaint procedures.',
    },
    stateNotes: [
      'Written complaint procedures required per Minn. Stat. 144A.474',
      'Clients must be informed how to file complaints with MDH',
      'MDH complaint hotline: (651) 201-4200',
      'Track all complaints and document resolutions',
      'Retaliation against complainants is prohibited',
    ],
  },

  {
    id: 'MN-A8',
    itemId: 'A8',
    stateCode: 'MN',
    title: 'Record Retention Policy',
    description: 'Minnesota requires minimum record retention periods',
    severity: 'medium',
    regulationCode: '144A.479',
    regulationName: 'Record Retention',
    agencyName: 'Minnesota Department of Health',
    agencyAbbr: 'MDH',
    agencyPhone: '(651) 201-4200',
    agencyUrl: 'https://www.health.state.mn.us/facilities/regulation/homecare',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain client and employee records?',
        inputType: 'select',
        options: [
          { value: '6plus', label: '6 years or longer' },
          { value: '5', label: '5 years' },
          { value: 'less5', label: 'Less than 5 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Minnesota requires retention of client records for at least 5 years after discharge',
      },
    ],
    scoringRules: [
      { condition: 'includes', value: ['6plus', '5'], result: 'COMPLIANT' },
      { condition: 'equals', value: 'less5', result: 'AT_RISK' },
      { condition: 'equals', value: 'unsure', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your record retention policy meets Minnesota requirements.',
      atRisk: 'Your retention period may not meet Minnesota requirements. Verify compliance.',
      nonCompliant: 'No formal record retention policy exists. This creates compliance risk.',
      unknown: 'Review record retention requirements.',
    },
    stateNotes: [
      'Minnesota requires 5-year retention after discharge for client records',
      'Personnel records should be retained 3 years after termination',
      'For minors, retain until age of majority plus retention period',
      'Electronic records must meet same retention requirements',
      'Secure storage and destruction procedures required',
    ],
  },

  {
    id: 'MN-A9',
    itemId: 'A9',
    stateCode: 'MN',
    title: 'Insurance Requirements',
    description: 'Minnesota requires specific insurance coverage',
    severity: 'high',
    regulationCode: '144A.472',
    regulationName: 'Insurance',
    agencyName: 'Minnesota Department of Health',
    agencyAbbr: 'MDH',
    agencyPhone: '(651) 201-4200',
    agencyUrl: 'https://www.health.state.mn.us/facilities/regulation/homecare',
    questions: [
      {
        id: 'q1',
        question: 'What insurance coverage does your agency maintain? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'gl', label: 'General liability insurance' },
          { value: 'prof', label: 'Professional liability insurance' },
          { value: 'wc', label: 'Workers\' compensation insurance' },
          { value: 'bond', label: 'Surety bond (if handling client funds)' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'When does your liability insurance expire?',
        inputType: 'date',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Insurance coverage meets Minnesota requirements per Minn. Stat. 144A.472.',
      atRisk: 'Insurance coverage may not meet all Minnesota requirements.',
      nonCompliant: 'Insurance does not meet Minnesota requirements.',
      unknown: 'Verify insurance meets Minn. Stat. 144A.472 requirements.',
    },
    stateNotes: [
      'Workers\' compensation required per Minn. Stat. 176',
      'MDH may require proof of liability insurance with license application',
      'Surety bond required if agency manages client funds',
    ],
  },

  // ============================================
  // SECTION B: BACKGROUND SCREENING
  // ============================================
  {
    id: 'MN-B1',
    itemId: 'B1',
    stateCode: 'MN',
    title: 'Background Study (Minn. Stat. 144A.476)',
    description: 'Minnesota requires background studies through NETStudy 2.0 for all staff',
    severity: 'high',
    regulationCode: '144A.476',
    regulationName: 'Background Studies',
    agencyName: 'Minnesota Department of Human Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(651) 431-6620',
    agencyUrl: 'https://mn.gov/dhs/partners-and-providers/licensing/background-studies/',
    questions: [
      {
        id: 'q1',
        question: 'For ALL currently employed staff, which of the following do you have on file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'netstudy', label: 'NETStudy 2.0 background study submission confirmation' },
          { value: 'clearance', label: 'Background study clearance letter from DHS' },
          { value: 'set_aside', label: 'Documentation of any set-aside or variance granted' },
          { value: 'fingerprints', label: 'Fingerprint-based check (for comprehensive license holders)' },
          { value: 'ongoing', label: 'Continuous criminal background monitoring' },
        ],
        required: true,
        helpText: 'Minnesota requires background studies through NETStudy 2.0 before any client contact per Minn. Stat. 144A.476',
      },
      {
        id: 'q2',
        question: 'Do any staff members begin working with clients BEFORE their background study clearance is received?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 3 },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Background study documentation is complete for all staff per Minn. Stat. 144A.476. NETStudy 2.0 clearances are on file.',
      atRisk: 'Background study documentation may be incomplete. Minnesota requires background studies BEFORE any client contact per Minn. Stat. 144A.476.',
      nonCompliant: 'Staff are working without proper background study clearance or are starting work before clearance is received. Employees working before background check clearance is a direct violation of Minn. Stat. 144A.476 and can result in license revocation. Contact DHS at (651) 431-6620.',
      unknown: 'Audit all staff files for NETStudy 2.0 documentation per Minn. Stat. 144A.476.',
    },
    stateNotes: [
      'Background studies conducted through DHS NETStudy 2.0 system',
      'Required for: owners, managerial officials, all employees with client contact',
      'Comprehensive licensees may require fingerprint-based FBI checks',
      'Set-asides available through DHS for certain disqualifications per Minn. Stat. 245C',
      'Background studies must be completed BEFORE any client contact',
    ],
  },

  {
    id: 'MN-B2',
    itemId: 'B2',
    stateCode: 'MN',
    title: 'Disqualifications and Set-Asides (Minn. Stat. 245C)',
    description: 'Understanding Minnesota disqualification criteria and set-aside process',
    severity: 'high',
    regulationCode: '245C',
    regulationName: 'Disqualifications',
    agencyName: 'Minnesota Department of Human Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(651) 431-6620',
    agencyUrl: 'https://mn.gov/dhs/partners-and-providers/licensing/background-studies/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have any staff with disqualification set-asides or variances?',
        inputType: 'select',
        options: [
          { value: 'none', label: 'No staff have disqualifications' },
          { value: 'setaside', label: 'Yes, some have approved set-asides' },
          { value: 'pending', label: 'Yes, some are pending set-aside decisions' },
          { value: 'unsure', label: 'Not sure' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'If you have staff with set-asides, are all required conditions being followed?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, all conditions documented and followed' },
          { value: 'no', label: 'No / Not applicable' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'setaside',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'none', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'setaside' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'pending', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'unsure', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Background study status and any set-aside conditions are properly documented per Minn. Stat. 245C.',
      atRisk: 'You have staff with pending disqualification decisions. They cannot work with clients until a set-aside is granted. Monitor status at NETStudy 2.0.',
      nonCompliant: 'You must know the disqualification status of all staff per Minn. Stat. 245C. Contact DHS immediately at (651) 431-6620.',
      unknown: 'Review Minn. Stat. 245C disqualification requirements with DHS.',
    },
    stateNotes: [
      'Permanent disqualifications for certain serious offenses per Minn. Stat. 245C.15',
      'Set-aside process available for other disqualifications per Minn. Stat. 245C.22',
      'DHS makes all disqualification and set-aside decisions',
      'Employers may request reconsideration of disqualification decisions',
    ],
  },

  {
    id: 'MN-B3',
    itemId: 'B3',
    stateCode: 'MN',
    title: 'OIG/Medicaid Exclusion List Check',
    description: 'Federal exclusion list verification required',
    severity: 'high',
    regulationCode: '42 CFR 1001',
    regulationName: 'Exclusion List',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the OIG and Medicaid exclusion lists for ALL employees?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, at hire and monthly thereafter' },
          { value: 'hire_only', label: 'At hire only, not ongoing' },
          { value: 'no', label: 'No / not checking this' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG and Medicaid exclusion list checks are conducted properly.',
      atRisk: 'Exclusion list checks should be ongoing, not just at hire. OIG recommends monthly verification.',
      nonCompliant: 'Exclusion list checks are not being conducted. This creates significant liability if you bill federal programs.',
      unknown: 'Verify exclusion list check process.',
    },
    stateNotes: [
      'OIG LEIE check required per 42 CFR 1001 for Medicare/Medicaid providers',
      'Free searchable database at exclusions.oig.hhs.gov',
      'Monthly verification recommended by OIG',
      'Check SAM.gov for vendors and contractors',
      'Document all checks with date, names checked, and results',
    ],
  },

  // ============================================
  // SECTION C: PERSONNEL FILES
  // ============================================
  {
    id: 'MN-C1',
    itemId: 'C1',
    stateCode: 'MN',
    title: 'Personnel File Requirements',
    description: 'Minnesota requires specific documents in each staff member\'s personnel file',
    severity: 'high',
    regulationCode: '144A.479',
    regulationName: 'Personnel Files',
    agencyName: 'Minnesota Department of Health',
    agencyAbbr: 'MDH',
    agencyPhone: '(651) 201-4200',
    agencyUrl: 'https://www.health.state.mn.us/facilities/regulation/homecare',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following documents do you maintain in EVERY staff member\'s personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'app', label: 'Employment application' },
          { value: 'id', label: 'Government-issued photo ID' },
          { value: 'background', label: 'NETStudy 2.0 background study clearance' },
          { value: 'tb', label: 'TB screening documentation' },
          { value: 'i9', label: 'I-9 Employment Eligibility Verification' },
          { value: 'training', label: 'Training documentation' },
          { value: 'competency', label: 'Competency evaluation records' },
          { value: 'job_desc', label: 'Job description' },
          { value: 'performance', label: 'Performance evaluations' },
          { value: 'licenses', label: 'Professional licenses (if applicable)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all required documentation per Minn. Stat. 144A.479.',
      atRisk: 'Some personnel files are missing required documents. Minnesota requires: background study clearance, TB screening, training records, and competency evaluations.',
      nonCompliant: 'Personnel files are missing critical documentation required by Minn. Stat. 144A.479. MDH surveyors will cite incomplete files.',
      unknown: 'Audit personnel files against Minn. Stat. 144A.479 requirements.',
    },
    stateNotes: [
      'Personnel file requirements per Minn. Stat. 144A.479',
      'NETStudy 2.0 background study clearance required before client contact',
      'TB screening documentation required at hire',
      'Training and competency documentation must be maintained',
      'I-9 required under federal law within 3 days of hire',
    ],
  },

  {
    id: 'MN-C2',
    itemId: 'C2',
    stateCode: 'MN',
    title: 'Professional License Verification',
    description: 'Verification of professional licenses for nurses and therapists',
    severity: 'high',
    regulationCode: '144A.479',
    regulationName: 'License Verification',
    agencyName: 'Minnesota Board of Nursing',
    agencyAbbr: 'MBN',
    agencyPhone: '(612) 317-3000',
    agencyUrl: 'https://mn.gov/boards/nursing/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your licensed staff (RNs, LPNs, therapists) have current Minnesota licenses verified?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, verify before hire and track renewals' },
          { value: 'hire_only', label: 'Verify at hire only' },
          { value: 'no', label: 'No formal verification process' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Professional license verification process meets requirements.',
      atRisk: 'License verification at hire only is insufficient. Implement ongoing verification.',
      nonCompliant: 'No license verification process creates significant compliance risk.',
      unknown: 'Verify license verification procedures are in place.',
    },
    stateNotes: [
      'Minnesota is a member of the Nurse Licensure Compact (NLC)',
      'Nurses with multistate licenses from other NLC states may practice in Minnesota',
      'Verify license status at mn.gov/boards/nursing/',
      'PT, OT, ST licenses verified through respective licensing boards',
    ],
  },

  {
    id: 'MN-C3',
    itemId: 'C3',
    stateCode: 'MN',
    title: 'HIPAA/Confidentiality Agreement',
    description: 'Signed HIPAA and confidentiality agreements required',
    severity: 'high',
    regulationCode: '45 CFR 164.530',
    regulationName: 'HIPAA',
    agencyName: 'HHS Office for Civil Rights',
    agencyAbbr: 'OCR',
    agencyPhone: '(800) 368-1019',
    agencyUrl: 'https://www.hhs.gov/hipaa/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your employees have a signed HIPAA/confidentiality agreement on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all employees have signed agreements' },
          { value: 'most', label: 'Most have it, some are missing' },
          { value: 'no', label: 'No / not consistently maintained' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All employees have signed HIPAA/confidentiality agreements on file.',
      atRisk: 'Some employees are missing signed HIPAA/confidentiality agreements.',
      nonCompliant: 'Personnel files are missing HIPAA/confidentiality agreements. This violates 45 CFR 164.530.',
      unknown: 'Verify all employees have signed HIPAA/confidentiality agreements on file.',
    },
    stateNotes: [
      'HIPAA confidentiality agreement required per 45 CFR 164.530',
      'Agreement should be signed during orientation before client contact',
      'Must cover protection of PHI and consequences of violations',
      'Minnesota Data Practices Act also applies to state-funded services',
      'Keep signed agreements in personnel file for duration of employment',
    ],
  },

  // ============================================
  // SECTION D: HEALTH & SAFETY
  // ============================================
  {
    id: 'MN-D1',
    itemId: 'D1',
    stateCode: 'MN',
    title: 'TB Screening',
    description: 'Minnesota requires TB risk assessment for all home care workers',
    severity: 'high',
    regulationCode: '144A.479',
    regulationName: 'TB Screening',
    agencyName: 'Minnesota Department of Health',
    agencyAbbr: 'MDH',
    agencyPhone: '(651) 201-4200',
    agencyUrl: 'https://www.health.state.mn.us/facilities/regulation/homecare',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your staff have current TB screening documentation on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have current TB screening' },
          { value: 'most', label: 'Most do, some may need updating' },
          { value: 'no', label: 'No / Not tracking this' },
        ],
        required: true,
        helpText: 'TB risk assessment or testing required at hire per Minnesota requirements',
      },
      {
        id: 'q2',
        question: 'What type of TB screening do you use?',
        inputType: 'select',
        options: [
          { value: 'risk_assessment', label: 'TB risk assessment questionnaire' },
          { value: 'skin_test', label: 'Tuberculin skin test (TST)' },
          { value: 'blood_test', label: 'Blood test (IGRA)' },
          { value: 'none', label: 'No screening process' },
        ],
      },
    ],
    scoringRules: [
      // 1. NON_COMPLIANT - Check for failure condition first
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // 2. COMPLIANT - All positive conditions met
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'includes', questionId: 'q2', value: ['risk_assessment', 'skin_test', 'blood_test'] },
      ], result: 'COMPLIANT' },
      // 3. AT_RISK - Partial compliance
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'TB screening documentation is current for all staff per Minn. Stat. 144A.479.',
      atRisk: 'Some TB screenings may need updating. Review all staff files.',
      nonCompliant: 'Staff are working without TB screening documentation. Immediately complete TB screening for all staff.',
      unknown: 'Verify TB screening status for all staff.',
    },
    stateNotes: [
      'TB risk assessment or testing required per Minn. Stat. 144A.479',
      'Acceptable methods: risk assessment questionnaire, TST skin test, or IGRA blood test',
      'Screening must be completed before client contact',
      'Positive results require chest X-ray and physician clearance',
      'Document results in personnel file',
    ],
  },

  {
    id: 'MN-D2',
    itemId: 'D2',
    stateCode: 'MN',
    title: 'Infection Control',
    description: 'Minnesota requires written infection control policies',
    severity: 'high',
    regulationCode: '144A.479',
    regulationName: 'Infection Control',
    agencyName: 'Minnesota Department of Health',
    agencyAbbr: 'MDH',
    agencyPhone: '(651) 201-4200',
    agencyUrl: 'https://www.health.state.mn.us/facilities/regulation/homecare',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written infection control policies that include: (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'hand_hygiene', label: 'Hand hygiene procedures' },
          { value: 'ppe', label: 'Personal protective equipment (PPE) use' },
          { value: 'isolation', label: 'Isolation precautions' },
          { value: 'cleaning', label: 'Cleaning and disinfection procedures' },
          { value: 'waste', label: 'Waste disposal procedures' },
          { value: 'reporting', label: 'Communicable disease reporting' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control policies meet Minnesota requirements per Minn. Stat. 144A.479.',
      atRisk: 'Infection control policies may be incomplete. Review and update to include all required elements.',
      nonCompliant: 'Infection control policies are missing critical elements. Update immediately.',
      unknown: 'Review infection control policies against requirements.',
    },
    stateNotes: [
      'Written infection control policies required per Minn. Stat. 144A.479',
      'Must include hand hygiene, PPE, and isolation precautions',
      'Staff must receive infection control training during orientation',
      'Annual training updates recommended',
      'Communicable disease reporting required to MDH',
    ],
  },

  {
    id: 'MN-D3',
    itemId: 'D3',
    stateCode: 'MN',
    title: 'Workers Compensation Insurance',
    description: 'Minnesota requires workers compensation insurance',
    severity: 'high',
    regulationCode: 'Minn. Stat. 176',
    regulationName: 'Workers Comp',
    agencyName: 'Minnesota Department of Labor and Industry',
    agencyAbbr: 'DLI',
    agencyPhone: '(651) 284-5005',
    agencyUrl: 'https://www.dli.mn.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain workers compensation insurance?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Workers compensation coverage is in place per Minn. Stat. 176.',
      atRisk: 'Workers compensation coverage should be verified.',
      nonCompliant: 'No workers compensation coverage. Minnesota law requires workers compensation insurance per Minn. Stat. 176. MDH will not issue a license without proof of coverage.',
      unknown: 'Review workers compensation requirements.',
    },
    stateNotes: [
      'MDH requires proof of workers compensation before issuing license',
      'Workers compensation is required for all employers in Minnesota',
      'Contact DLI at (651) 284-5005 for compliance questions',
    ],
  },

  {
    id: 'MN-D4',
    itemId: 'D4',
    stateCode: 'MN',
    title: 'Hepatitis B Vaccination Offer',
    description: 'OSHA requires offer of Hepatitis B vaccination to at-risk employees',
    severity: 'medium',
    regulationCode: '29 CFR 1910.1030',
    regulationName: 'Bloodborne Pathogens',
    agencyName: 'Occupational Safety and Health Administration',
    agencyAbbr: 'OSHA',
    agencyPhone: '(800) 321-6742',
    agencyUrl: 'https://www.osha.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you offer Hepatitis B vaccination to employees with potential occupational exposure?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, offered to all at-risk employees at no cost' },
          { value: 'some', label: 'Offered to some employees' },
          { value: 'no', label: 'Not offered' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you document employee acceptance or declination of the vaccine?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'some', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Hepatitis B vaccination program meets OSHA 29 CFR 1910.1030 requirements.',
      atRisk: 'Hepatitis B vaccination offer should be extended to all at-risk employees.',
      nonCompliant: 'Hepatitis B vaccination is not being offered. This violates 29 CFR 1910.1030.',
      unknown: 'Review Hepatitis B vaccination requirements.',
    },
    stateNotes: [
      'Hepatitis B vaccination required per OSHA 29 CFR 1910.1030',
      'Must be offered within 10 days of employment for at-risk employees',
      'Vaccination is free to employees under OSHA standards',
      'Declination must be documented using OSHA-compliant form',
      'Minnesota OSHA enforces bloodborne pathogens standard',
    ],
  },

  // ============================================
  // SECTION E: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'MN-E1',
    itemId: 'E1',
    stateCode: 'MN',
    title: 'Orientation Training (Minn. Stat. 144A.479)',
    description: 'Minnesota requires orientation training before client contact',
    severity: 'high',
    regulationCode: '144A.479',
    regulationName: 'Orientation',
    agencyName: 'Minnesota Department of Health',
    agencyAbbr: 'MDH',
    agencyPhone: '(651) 201-4200',
    agencyUrl: 'https://www.health.state.mn.us/facilities/regulation/homecare',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL new staff complete orientation training covering the following BEFORE client contact? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'bill_of_rights', label: 'Home Care Bill of Rights (Minn. Stat. 144A.44)' },
          { value: 'maltreatment', label: 'Maltreatment reporting requirements (Minn. Stat. 626.557)' },
          { value: 'emergency', label: 'Emergency procedures' },
          { value: 'infection', label: 'Infection control' },
          { value: 'privacy', label: 'Privacy and data practices (HIPAA)' },
          { value: 'scope', label: 'Scope of services and limitations' },
        ],
        required: true,
        helpText: 'Minn. Stat. 144A.479 requires orientation before first client contact',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Orientation training program meets Minnesota Minn. Stat. 144A.479 requirements.',
      atRisk: 'Orientation training may be incomplete. Minn. Stat. 144A.479 requires training on Bill of Rights, maltreatment reporting, and emergency procedures BEFORE first client contact.',
      nonCompliant: 'Staff are providing services without required orientation training. This violates Minn. Stat. 144A.479.',
      unknown: 'Review orientation training program against Minn. Stat. 144A.479 requirements.',
    },
    stateNotes: [
      'Orientation training required per Minn. Stat. 144A.479 before client contact',
      'Must include Home Care Bill of Rights (Minn. Stat. 144A.44)',
      'Maltreatment reporting (Minn. Stat. 626.557) training required',
      'Emergency procedures and infection control must be covered',
      'Document orientation with dates, topics, and employee signature',
    ],
  },

  {
    id: 'MN-E2',
    itemId: 'E2',
    stateCode: 'MN',
    title: 'Ongoing Training (Minn. Stat. 144A.479)',
    description: 'Minnesota requires ongoing training for home care workers',
    severity: 'medium',
    regulationCode: '144A.479',
    regulationName: 'Ongoing Training',
    agencyName: 'Minnesota Department of Health',
    agencyAbbr: 'MDH',
    agencyPhone: '(651) 201-4200',
    agencyUrl: 'https://www.health.state.mn.us/facilities/regulation/homecare',
    questions: [
      {
        id: 'q1',
        question: 'Do all staff receive ongoing/annual training on required topics?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff complete annual training' },
          { value: 'most', label: 'Most complete training, some are behind' },
          { value: 'no', label: 'No structured annual training program' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which topics does your ongoing training cover? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'maltreatment', label: 'Maltreatment reporting (annual required)' },
          { value: 'infection', label: 'Infection control updates' },
          { value: 'safety', label: 'Safety procedures' },
          { value: 'client_specific', label: 'Client-specific care needs' },
          { value: 'documentation', label: 'Documentation requirements' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Ongoing training program meets Minnesota requirements per Minn. Stat. 144A.479.',
      atRisk: 'Some staff may be behind on training. Review training records and schedule catch-up training.',
      nonCompliant: 'No structured ongoing training program exists. This violates Minn. Stat. 144A.479.',
      unknown: 'Review ongoing training program against Minn. Stat. 144A.479 requirements.',
    },
    stateNotes: [
      'Annual training required per Minn. Stat. 144A.479',
      'Maltreatment reporting training must be completed annually',
      'Training topics should address agency quality improvement needs',
      'Client-specific training required for specialized care needs',
      'Document all training with dates, topics, and hours completed',
    ],
  },

  {
    id: 'MN-E3',
    itemId: 'E3',
    stateCode: 'MN',
    title: 'Maltreatment Reporting Training (Minn. Stat. 626.557)',
    description: 'Minnesota requires annual training on vulnerable adult and child maltreatment reporting',
    severity: 'high',
    regulationCode: '626.557',
    regulationName: 'Adult Maltreatment',
    agencyName: 'Minnesota Adult Abuse Reporting Center',
    agencyAbbr: 'MAARC',
    agencyPhone: '(844) 880-1574',
    agencyUrl: 'https://mn.gov/dhs/people-we-serve/adults/services/adult-protection/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL staff receive annual training on maltreatment reporting?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff receive annual training' },
          { value: 'most', label: 'Most receive it' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do staff know how to report suspected maltreatment to MAARC?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - call (844) 880-1574' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff receive annual maltreatment reporting training per Minn. Stat. 626.557.',
      atRisk: 'Some staff may be missing maltreatment reporting training. All staff must know how to report to MAARC at (844) 880-1574.',
      nonCompliant: 'Staff are not receiving required annual maltreatment reporting training. This violates Minn. Stat. 626.557.',
      unknown: 'Verify all staff have received maltreatment reporting training per Minn. Stat. 626.557.',
    },
    stateNotes: [
      'Home care workers are mandated reporters under Minn. Stat. 626.557',
      'Must report suspected abuse, neglect, or financial exploitation',
      'Report to MAARC: (844) 880-1574',
      'Failure to report is a misdemeanor',
      'Training must be provided annually',
    ],
  },

  {
    id: 'MN-E4',
    itemId: 'E4',
    stateCode: 'MN',
    title: 'Child Maltreatment Reporting Training (Minn. Stat. 626.556)',
    description: 'Training on child abuse and neglect reporting requirements',
    severity: 'high',
    regulationCode: '626.556',
    regulationName: 'Child Maltreatment',
    agencyName: 'Minnesota Department of Human Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(651) 431-4661',
    agencyUrl: 'https://mn.gov/dhs/people-we-serve/children-and-families/services/child-protection/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL employees receive training on child maltreatment recognition and reporting?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all employees receive training' },
          { value: 'most', label: 'Most employees, some may be missing' },
          { value: 'no', label: 'No formal training' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All employees receive child maltreatment reporting training per Minn. Stat. 626.556.',
      atRisk: 'Some employees may be missing training.',
      nonCompliant: 'No child maltreatment reporting training program.',
      unknown: 'Review training requirements.',
    },
    stateNotes: [
      'Home care workers are mandated reporters for child maltreatment',
      'Report to local child protection or law enforcement',
      'Failure to report is a misdemeanor per Minn. Stat. 626.556, subd. 6',
    ],
  },

  {
    id: 'MN-E5',
    itemId: 'E5',
    stateCode: 'MN',
    title: 'Competency Evaluation',
    description: 'Minnesota requires competency evaluation for direct care staff',
    severity: 'high',
    regulationCode: '144A.479',
    regulationName: 'Competency',
    agencyName: 'Minnesota Department of Health',
    agencyAbbr: 'MDH',
    agencyPhone: '(651) 201-4200',
    agencyUrl: 'https://www.health.state.mn.us/facilities/regulation/homecare',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL direct care staff have documented competency evaluations on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have completed competency evaluations' },
          { value: 'most', label: 'Most staff have them' },
          { value: 'no', label: 'No / not tracking this' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How often do you conduct competency evaluations?',
        inputType: 'select',
        options: [
          { value: 'hire_annual', label: 'At hire and annually' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'none', label: 'No regular schedule' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'hire_annual' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff have documented competency evaluations per Minn. Stat. 144A.479.',
      atRisk: 'Some competency evaluations may be missing.',
      nonCompliant: 'Staff are working without documented competency evaluations. This violates Minn. Stat. 144A.479.',
      unknown: 'Verify all staff have competency evaluations.',
    },
    stateNotes: [
      'Competency evaluation required per Minn. Stat. 144A.479',
      'Must be completed before staff provide services independently',
      'Evaluations should cover all assigned tasks',
      'Annual competency evaluations recommended',
      'RN or qualified supervisor must conduct evaluations',
    ],
  },

  // ============================================
  // SECTION F: CARE DELIVERY
  // ============================================
  {
    id: 'MN-F1',
    itemId: 'F1',
    stateCode: 'MN',
    title: 'Home Care Bill of Rights (Minn. Stat. 144A.44)',
    description: 'Clients must receive copy of Home Care Bill of Rights',
    severity: 'high',
    regulationCode: '144A.44',
    regulationName: 'Bill of Rights',
    agencyName: 'Minnesota Department of Health',
    agencyAbbr: 'MDH',
    agencyPhone: '(651) 201-4200',
    agencyUrl: 'https://www.health.state.mn.us/facilities/regulation/homecare',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide ALL clients with a copy of the Minnesota Home Care Bill of Rights?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all clients receive it at intake' },
          { value: 'most', label: 'Most clients receive it' },
          { value: 'no', label: 'No / not consistently' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you document client acknowledgment of receiving the Bill of Rights?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, signed acknowledgment on file' },
          { value: 'no', label: 'No documentation' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All clients receive the Home Care Bill of Rights with documented acknowledgment per Minn. Stat. 144A.44.',
      atRisk: 'Some clients may not be receiving the Bill of Rights or acknowledgment is not documented.',
      nonCompliant: 'Clients are not receiving the Home Care Bill of Rights. This violates Minn. Stat. 144A.44.',
      unknown: 'Verify Bill of Rights distribution per Minn. Stat. 144A.44.',
    },
    stateNotes: [
      'Bill of Rights must be provided before services begin or at first visit',
      'Must be provided in a language the client understands',
      'MDH provides template Bill of Rights on website',
      'Violations can result in fines per Minn. Stat. 144A.474',
    ],
  },

  {
    id: 'MN-F2',
    itemId: 'F2',
    stateCode: 'MN',
    title: 'Supervision of Direct Care Staff',
    description: 'Minnesota requires appropriate supervision of unlicensed personnel',
    severity: 'high',
    regulationCode: '144A.4791',
    regulationName: 'Supervision',
    agencyName: 'Minnesota Department of Health',
    agencyAbbr: 'MDH',
    agencyPhone: '(651) 201-4200',
    agencyUrl: 'https://www.health.state.mn.us/facilities/regulation/homecare',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide appropriate supervision of unlicensed personnel as required?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, per regulatory requirements' },
          { value: 'partial', label: 'Sometimes, not consistently' },
          { value: 'no', label: 'No supervision program' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'For Comprehensive licensees: Does an RN supervise delegated nursing tasks?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
          { value: 'na', label: 'Not applicable (Basic license)' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Supervision meets Minnesota requirements per Minn. Stat. 144A.4791.',
      atRisk: 'Supervision may not meet required frequency or documentation standards.',
      nonCompliant: 'No supervision program exists. This violates Minn. Stat. 144A.4791.',
      unknown: 'Review supervision requirements.',
    },
    stateNotes: [
      'Comprehensive licensees must have RN supervision of delegated nursing tasks',
      'Supervision requirements vary by service type and client acuity',
      'Documentation of supervision required',
    ],
  },

  {
    id: 'MN-F3',
    itemId: 'F3',
    stateCode: 'MN',
    title: 'Service Plan Documentation',
    description: 'Minnesota requires documented service plans for all clients',
    severity: 'high',
    regulationCode: '144A.4791',
    regulationName: 'Service Plans',
    agencyName: 'Minnesota Department of Health',
    agencyAbbr: 'MDH',
    agencyPhone: '(651) 201-4200',
    agencyUrl: 'https://www.health.state.mn.us/facilities/regulation/homecare',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written service plan for EVERY client before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal service plan process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How often do you review and update client service plans?',
        inputType: 'select',
        options: [
          { value: 'annually', label: 'At least annually and when client needs change' },
          { value: 'when_needed', label: 'Only when client needs change' },
          { value: 'never', label: 'No regular review process' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'annually' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Service plans are documented and reviewed per Minn. Stat. 144A.4791.',
      atRisk: 'Service plans may be created after services begin or not regularly reviewed.',
      nonCompliant: 'Services are being provided without written service plans. This violates Minn. Stat. 144A.4791.',
      unknown: 'Review service plan process against requirements.',
    },
    stateNotes: [
      'Written service plan required per Minn. Stat. 144A.4791 before services begin',
      'Plan must include specific services, frequency, and goals',
      'Client or representative must sign the service plan',
      'Review and update at least annually or when needs change',
      'Service plan must be accessible to staff providing care',
    ],
  },

  {
    id: 'MN-F4',
    itemId: 'F4',
    stateCode: 'MN',
    title: 'Visit Documentation',
    description: 'Minnesota requires documentation of each client visit',
    severity: 'high',
    regulationCode: '144A.479',
    regulationName: 'Visit Documentation',
    agencyName: 'Minnesota Department of Health',
    agencyAbbr: 'MDH',
    agencyPhone: '(651) 201-4200',
    agencyUrl: 'https://www.health.state.mn.us/facilities/regulation/homecare',
    questions: [
      {
        id: 'q1',
        question: 'Do you document each client visit with required information?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all visits documented with date, time, services, and signature' },
          { value: 'most', label: 'Most visits documented' },
          { value: 'no', label: 'No consistent documentation' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How quickly are visit notes completed after the visit?',
        inputType: 'select',
        options: [
          { value: 'same_day', label: 'Same day' },
          { value: 'within_48', label: 'Within 48 hours' },
          { value: 'within_week', label: 'Within one week' },
          { value: 'varies', label: 'Varies / no standard' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'includes', questionId: 'q2', value: ['same_day', 'within_48'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Visit documentation meets Minnesota requirements per Minn. Stat. 144A.479.',
      atRisk: 'Visit documentation is incomplete or delayed.',
      nonCompliant: 'Visit documentation is inadequate. This violates Minn. Stat. 144A.479.',
      unknown: 'Review visit documentation requirements.',
    },
    stateNotes: [
      'Visit documentation required per Minn. Stat. 144A.479',
      'Must include date, arrival/departure times, and services provided',
      'Staff signature required for each visit',
      'Documentation should be completed same day or within 48 hours',
      'Observations and changes in client condition must be recorded',
    ],
  },

  // ============================================
  // SECTION G: DOCUMENTATION/ADMIN
  // ============================================
  {
    id: 'MN-G1',
    itemId: 'G1',
    stateCode: 'MN',
    title: 'Document Expiration Tracking',
    description: 'System to track license, certification, and clearance expirations',
    severity: 'high',
    regulationCode: '144A.479',
    regulationName: 'Document Tracking',
    agencyName: 'Minnesota Department of Health',
    agencyAbbr: 'MDH',
    agencyPhone: '(651) 201-4200',
    agencyUrl: 'https://www.health.state.mn.us/facilities/regulation/homecare',
    questions: [
      {
        id: 'q1',
        question: 'Do you track expiration dates for all required documents?',
        inputType: 'checklist',
        options: [
          { value: 'provider_license', label: 'Home care provider license (annual)' },
          { value: 'insurance', label: 'Liability insurance' },
          { value: 'tb', label: 'TB screenings' },
          { value: 'cpr', label: 'CPR/First Aid certifications' },
          { value: 'professional', label: 'Professional licenses (RN, LPN, etc.)' },
          { value: 'training', label: 'Training due dates' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you receive advance reminders before documents expire?',
        inputType: 'select',
        options: [
          { value: 'auto', label: 'Yes, automated reminders 30+ days in advance' },
          { value: 'manual', label: 'Yes, manual review monthly' },
          { value: 'no', label: 'No proactive tracking' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'includes', questionId: 'q2', value: ['auto', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Document expiration tracking is comprehensive with proactive reminders.',
      atRisk: 'Expiration tracking is incomplete. Implement tracking to prevent compliance lapses.',
      nonCompliant: 'No systematic document tracking in place. This creates significant compliance risk.',
      unknown: 'Implement document expiration tracking system.',
    },
    stateNotes: [
      'Home care provider license renews annually per Minn. Stat. 144A.472',
      'Professional licenses (RN, LPN) have specific renewal dates',
      'TB screenings should be tracked for expiration',
      'Training due dates should be monitored proactively',
      '30-60 day advance reminders recommended to prevent lapses',
    ],
  },

  {
    id: 'MN-G2',
    itemId: 'G2',
    stateCode: 'MN',
    title: 'Complaint Tracking',
    description: 'Minnesota requires written complaint procedures and tracking',
    severity: 'medium',
    regulationCode: '144A.474',
    regulationName: 'Complaints',
    agencyName: 'Minnesota Department of Health',
    agencyAbbr: 'MDH',
    agencyPhone: '(651) 201-4200',
    agencyUrl: 'https://www.health.state.mn.us/facilities/regulation/homecare',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written complaint investigation procedures?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you document all complaints and their resolutions?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, all complaints are documented' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'no', label: 'No formal tracking' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'always' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Complaint procedures and tracking meet Minnesota requirements per Minn. Stat. 144A.474.',
      atRisk: 'Complaint tracking is inconsistent. Document all complaints and resolutions.',
      nonCompliant: 'No formal complaint procedures exist. This violates Minn. Stat. 144A.474.',
      unknown: 'Review complaint procedures against requirements.',
    },
    stateNotes: [
      'Written complaint procedures required per Minn. Stat. 144A.474',
      'Document all complaints with date, description, and resolution',
      'Inform clients how to file complaints with MDH',
      'Track complaint trends for quality improvement',
      'Retaliation against complainants is prohibited',
    ],
  },

  {
    id: 'MN-G3',
    itemId: 'G3',
    stateCode: 'MN',
    title: 'Electronic Records Security',
    description: 'Security measures for electronic health records',
    severity: 'high',
    regulationCode: '45 CFR 164',
    regulationName: 'HIPAA Security',
    agencyName: 'HHS Office for Civil Rights',
    agencyAbbr: 'OCR',
    agencyPhone: '(800) 368-1019',
    agencyUrl: 'https://www.hhs.gov/hipaa/',
    questions: [
      {
        id: 'q1',
        question: 'If you maintain electronic records, do you have appropriate security measures?',
        inputType: 'select',
        options: [
          { value: 'comprehensive', label: 'Yes, password protection, encryption, backups, and access controls' },
          { value: 'basic', label: 'Basic security measures only' },
          { value: 'no', label: 'No security measures' },
          { value: 'na', label: 'N/A - paper records only' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'includes', value: ['comprehensive', 'na'], result: 'COMPLIANT' },
      { condition: 'equals', value: 'basic', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Electronic records security measures are appropriate per 45 CFR 164.',
      atRisk: 'Electronic records security should be enhanced to meet HIPAA requirements.',
      nonCompliant: 'Electronic records lack required security measures. This violates 45 CFR 164.',
      unknown: 'Review electronic records security requirements.',
    },
    stateNotes: [
      'HIPAA Security Rule (45 CFR 164) applies to electronic PHI',
      'Password protection and encryption required for electronic records',
      'Access controls must limit PHI access to authorized personnel',
      'Regular data backups required',
      'Security risk assessment should be conducted annually',
    ],
  },
];

export default MINNESOTA_QUESTIONS;